import { Box, Card, CardContent, Typography } from "@mui/material";
import InfoPopper from "../../../../../components/popper/InfoPopper";
import { YearByYearChart } from "./YearByYearChart";
import { AllSeriesType } from "@mui/x-charts";
import { EcoEfficiencyDashboardContext } from "../../../../../contexts/EcoEfficiencyDashboardContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { common, overview, reduce } from "../../../../../i18n";
import ChartCheckbox from "../../../../../components/chart/ChartCheckbox";
import { DashedLineIcon } from "../../../../../components/icon/DashedLineIcon";
import TrendLinePlan from "../../../../../components/icon/TrendLinePlan";

const EcoYoyChart = () => {
  const { t } = useTranslation([common, overview, reduce], {
    nsMode: "fallback",
  });

  const { yearly, selectedEcoEfficiency } = EcoEfficiencyDashboardContext();

  const [series, setSeries] = useState<AllSeriesType[]>([]);

  const [labels, setLabels] = useState<string[]>([]);

  const [filter, setFilter] = useState<string[]>([]);

  const toggleFilter = (key: string) => {
    setFilter((prev) => {
      const isSelected = prev.includes(key);
      return isSelected ? prev.filter((f) => f !== key) : [...prev, key];
    });
  };

  useEffect(() => {
    if (!yearly || !selectedEcoEfficiency) return;
    try {
      const years = Array.from(
        { length: 6 },
        (_, i) => selectedEcoEfficiency!.year + i,
      );
      setLabels(years.map(String));

      // เตรียม Map เพื่ออ่านค่าเร็ว

      const yearlyMap = new Map(yearly!.years.map((y) => [y.year, y]));

      // ดึงค่าจากปีที่ fix ไว้
      const ecoEfficiency = years.map(
        (year) => yearlyMap.get(year)?.ecoEfficiencyNormalized ?? 0,
      );

      setSeries([
        {
          type: "bar",
          data: [...ecoEfficiency],
          label: `${t("Actual")}`,
          color: "#a5d8ff",
        },
      ]);
    } catch (error) {
      console.error(error);
    }
  }, [yearly, selectedEcoEfficiency, t]);

  useEffect(() => {
    setFilter(["baseYear", ...series.map((s: any) => s.label ?? "")]);
  }, [series]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        padding: "24px",
        ".MuiCardContent-root": {
          padding: "0px !important",
        },
      }}
    >
      <CardContent>
        <Box display={"flex"} flexDirection={"column"} gap={"20px"}>
          <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
            <Typography variant="h3" flexGrow={1}>
              {t("Year-By-Year")}
              <InfoPopper text={t("Year-By-Year-Description")} />
            </Typography>
          </Box>
          {/* Chart label */}

          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            gap={"24px"}
            flexWrap={"wrap"}
            style={{ width: "100%" }}
          >
            {selectedEcoEfficiency && (
              <ChartCheckbox
                key={`baseYear`}
                text={`${t("Base-Year")} (${
                  selectedEcoEfficiency!.baseYear ?? ""
                })`}
                color={""}
                checked={filter.includes("baseYear")}
                onChange={(_, checked) => toggleFilter("baseYear")}
                icon={<DashedLineIcon />}
              />
            )}
            {series.map((e: any) => (
              <ChartCheckbox
                key={`${e.label}`}
                text={e.label ?? ""}
                color={e.color ?? ""}
                checked={filter.includes(`${e.label}`)}
                onChange={(_, checked) => toggleFilter(`${e.label}`)}
                icon={
                  e.label === t("Plan") ? (
                    <TrendLinePlan color="var(--green)" />
                  ) : undefined
                }
              />
            ))}
          </Box>
          <YearByYearChart
            series={series.filter((s: any) => filter.includes(s.label ?? ""))}
            labels={labels}
            showBaseLine={filter.includes("baseYear")}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default EcoYoyChart;
